/**
 * FeedTopicsModal — multi-select topic chips → userFeedTags.
 * Web port of client-native FeedTopicsScreen + FeedTagsSelector.
 *
 * Description + TagChip grid + footer hint + Save (only when changed). Uses the
 * shared @/ui TagChip and the track-local getFeedTags endpoint.
 */
import { FC, useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { colors, TagChip } from "@/ui";
import ProfileModal from "../ProfileModal";
import { callWithAuth } from "@/api/Api";
import { useUserSettings } from "@/hooks/useUserSettings";
import { getFeedTags } from "@/feed/api/feedApi";

interface FeedTopicsModalProps {
  open: boolean;
  onClose: () => void;
}

const FeedTopicsModal: FC<FeedTopicsModalProps> = ({ open, onClose }) => {
  const { settings, updateSettings } = useUserSettings();
  const [selected, setSelected] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setSelected(settings?.userFeedTags ?? []);
  }, [open, settings?.userFeedTags]);

  const tagsQuery = useQuery({
    queryKey: ["feedTags"],
    queryFn: () => callWithAuth(getFeedTags),
    enabled: open,
  });
  const tags = tagsQuery.data?.tags ?? [];

  const toggle = (name: string) =>
    setSelected((prev) =>
      prev.includes(name) ? prev.filter((t) => t !== name) : [...prev, name]
    );

  const original = settings?.userFeedTags ?? [];
  const changed =
    selected.length !== original.length ||
    selected.some((t) => !original.includes(t));

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateSettings({ userFeedTags: selected });
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <ProfileModal open={open} onClose={onClose}>
      <span className="text-lg font-bold" style={{ color: colors.primary }}>
        Video Topics
      </span>
      <p
        className="text-sm"
        style={{ color: colors.secondary, textAlign: "center", marginTop: 8 }}
      >
        Select topics you're interested in. Videos matching your selected topics
        will appear more often in your feed.
      </p>

      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: 10,
          justifyContent: "center",
          padding: "12px 0",
          maxHeight: 260,
          overflowY: "auto",
        }}
      >
        {tagsQuery.isLoading ? (
          <span className="text-sm" style={{ color: colors.muted }}>Loading…</span>
        ) : tagsQuery.isError ? (
          // iOS FeedTagsSelector error state.
          <span className="text-sm" style={{ color: colors.error }}>
            Failed to load topics. Please try again.
          </span>
        ) : tags.length === 0 ? (
          // iOS FeedTagsSelector empty state.
          <span className="text-sm" style={{ color: colors.muted }}>
            No topics available
          </span>
        ) : (
          tags.map((tag) => (
            <TagChip
              key={tag.name}
              label={tag.displayName}
              selected={selected.includes(tag.name)}
              onPress={() => toggle(tag.name)}
            />
          ))
        )}
      </div>

      <p className="text-xs" style={{ color: colors.muted, textAlign: "center" }}>
        {selected.length === 0
          ? "No topics selected - you'll see videos from all topics."
          : `${selected.length} topic${selected.length > 1 ? "s" : ""} selected`}
      </p>

      {changed && (
        <button
          type="button"
          onClick={handleSave}
          disabled={saving}
          style={{
            marginTop: 16,
            backgroundColor: colors.blue,
            color: colors.white,
            padding: "12px 24px",
            borderRadius: 24,
            border: "none",
            cursor: "pointer",
            opacity: saving ? 0.6 : 1,
          }}
        >
          <span className="text-base font-semibold">Save</span>
        </button>
      )}
    </ProfileModal>
  );
};

export default FeedTopicsModal;
